import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Search,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  X,
  User,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { members } from '../api/endpoints.js'

const STATUS_BADGE = {
  active: 'badge-active',
  alert: 'badge-alert',
  suspended: 'badge-suspended',
  cancelled: 'badge-cancelled',
}

function StatusBadge({ status }) {
  const cls = STATUS_BADGE[status] ?? 'badge-cancelled'
  return (
    <span className={cls}>
      {status ?? 'unknown'}
    </span>
  )
}

const EMPTY_MEMBER = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  dob: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
}

function AddMemberModal({ onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY_MEMBER)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.first_name || !form.last_name || !form.email) {
      setError('First name, last name, and email are required.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const res = await members.create(form)
      toast.success('Member created!')
      onSuccess(res.data)
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          'Failed to create member.',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-surface-2 border border-surface-3 rounded-2xl w-full max-w-lg shadow-2xl animate-fade-in">
        <div className="flex items-center justify-between p-5 border-b border-surface-3">
          <h2 className="text-lg font-semibold text-text-primary">
            Add New Member
          </h2>
          <button
            onClick={onClose}
            className="text-text-dim hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <p className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">First Name *</label>
              <input
                name="first_name"
                className="input-field"
                value={form.first_name}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="label">Last Name *</label>
              <input
                name="last_name"
                className="input-field"
                value={form.last_name}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          <div>
            <label className="label">Email *</label>
            <input
              name="email"
              type="email"
              className="input-field"
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label className="label">Phone</label>
            <input
              name="phone"
              type="tel"
              className="input-field"
              value={form.phone}
              onChange={handleChange}
            />
          </div>
          <div>
            <label className="label">Date of Birth</label>
            <input
              name="dob"
              type="date"
              className="input-field"
              value={form.dob}
              onChange={handleChange}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Emergency Contact</label>
              <input
                name="emergency_contact_name"
                className="input-field"
                placeholder="Name"
                value={form.emergency_contact_name}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="label">Emergency Phone</label>
              <input
                name="emergency_contact_phone"
                type="tel"
                className="input-field"
                placeholder="Phone"
                value={form.emergency_contact_phone}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center gap-2"
            >
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving…
                </>
              ) : (
                'Add Member'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function MembersPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showAdd, setShowAdd] = useState(false)
  const PAGE_SIZE = 25

  const { data, isLoading, error } = useQuery({
    queryKey: ['members', { search, page }],
    queryFn: () =>
      members
        .list({ q: search || undefined, page, per_page: PAGE_SIZE })
        .then((r) => r.data),
    keepPreviousData: true,
  })

  const items = data?.items ?? data ?? []
  const total = data?.total ?? items.length
  const totalPages = Math.ceil(total / PAGE_SIZE)

  const handleSearchChange = (e) => {
    setSearch(e.target.value)
    setPage(1)
  }

  const handleAddSuccess = (member) => {
    setShowAdd(false)
    qc.invalidateQueries(['members'])
    navigate(`/members/${member.id}`)
  }

  const fmtDate = (d) => {
    if (!d) return '—'
    try {
      return format(new Date(d), 'MMM d, yyyy')
    } catch {
      return d
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Members</h1>
          {total > 0 && (
            <p className="text-text-muted text-sm mt-0.5">
              {total.toLocaleString()} total member{total !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="btn-primary flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          Add Member
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-dim" />
        <input
          type="search"
          className="input-field pl-10"
          placeholder="Search name, email, phone…"
          value={search}
          onChange={handleSearchChange}
        />
      </div>

      {error && (
        <p className="text-red-400 text-sm">Failed to load members.</p>
      )}

      {/* Table */}
      <div className="table-container">
        <table className="table-base">
          <thead className="table-head">
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Plan</th>
              <th>Next Bill</th>
              <th>Member Since</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-t border-surface-3">
                  {Array.from({ length: 6 }).map((_, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 bg-surface-3 rounded animate-pulse w-3/4" />
                    </td>
                  ))}
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-text-dim">
                  <User className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  {search
                    ? 'No members match your search.'
                    : 'No members yet. Add one to get started.'}
                </td>
              </tr>
            ) : (
              items.map((m) => (
                <tr
                  key={m.id}
                  className="table-row"
                  onClick={() => navigate(`/members/${m.id}`)}
                >
                  <td className="font-medium">
                    {m.full_name?.trim() ||
                      `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() ||
                      '—'}
                  </td>
                  <td className="text-text-muted">{m.email}</td>
                  <td>
                    <StatusBadge status={m.status} />
                  </td>
                  <td className="text-text-muted">
                    {m.plan_name ?? '—'}
                  </td>
                  <td className="text-text-muted">
                    {fmtDate(m.next_billing_date)}
                  </td>
                  <td className="text-text-muted">
                    {fmtDate(m.member_since ?? m.created_at)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-text-muted">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary p-2 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn-secondary p-2 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {showAdd && (
        <AddMemberModal
          onClose={() => setShowAdd(false)}
          onSuccess={handleAddSuccess}
        />
      )}
    </div>
  )
}
